import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
} from "react";
import pRetry from "p-retry";
import { env, isProd } from "../../../env";

interface JimoProviderProps {
  children: ReactNode;
}

interface JimoContextValue {
  identifyUser: (userId?: string, params?: object) => void;
}

const JimoContext = createContext<JimoContextValue | undefined>(undefined);

export const JimoProvider = ({ children }: JimoProviderProps) => {
  const identifyUser = useCallback((userId?: string, params?: object) => {
    const track = async () => {
      const jimo = getJimo();

      if (userId && params) {
        const user = params as {
          client?: unknown;
          first_name?: string;
          last_name?: string;
          created_at?: Date;
          email?: string;
          role?: string;
        };

        const client = user.client as unknown as {
          company: string;
          uuid: string;
        };

        jimo?.push(["do", "identify", [userId]]);

        jimo?.push(["set", "user:email", [user.email]]);

        jimo?.push([
          "set",
          "user:name",
          [`${user.first_name} ${user.last_name}`],
        ]);

        jimo?.push([
          "set",
          "user:attributes",
          [
            {
              environment: process.env.NODE_ENV,
              company: client.company,
              clientId: client.uuid,
              userUuid: userId,
              role: user.role,
              createdAt: user.created_at,
            },
          ],
        ]);
      }
    };
    pRetry(track, {
      retries: 5,
    });
  }, []);

  useEffect(() => {
    const jimoHasAlreadyBeenInitialized = "jimo" in window;
    const jimoProjectIdSet = !!env?.JIMO_PROJECT_ID;

    const shouldNotInitializeJimo =
      jimoHasAlreadyBeenInitialized || !jimoProjectIdSet;

    if (shouldNotInitializeJimo) {
      return;
    }

    (window as any).jimo = [];

    const s = document.createElement("script");
    s.type = "text/javascript";
    s.async = true;
    s.src = "https://undercity.usejimo.com/jimo-invader.js";
    window["JIMO_PROJECT_ID"] = env.JIMO_PROJECT_ID;
    document.getElementsByTagName("head")[0].appendChild(s);
  }, [env?.JIMO_PROJECT_ID]);

  return (
    <JimoContext.Provider value={{ identifyUser }}>
      {children}
    </JimoContext.Provider>
  );
};

function getJimo() {
  if (!("jimo" in window)) {
    if (isProd()) {
      throw new Error("No jimo");
    }
    console.warn("Jimo is not available in non-prod environments");
    return null;
  }
  return window["jimo" as any] as any;
}

export const useJimo = () => {
  const contextValue = useContext(JimoContext);
  if (!contextValue) {
    throw new Error(
      "useJimo must be used within a JimoProvider. Wrap a parent component in <JimoProvider> to fix this error.",
    );
  }
  return contextValue;
};
